import { DocumentsItem } from '@networkteam/giraffe';
import classNames from 'classnames';
import { useRef, useState } from 'react';
import { Minus, Plus } from './icons';
import { ThemeColor, themeColors } from '@/lib/config/colors';

type NavigationProps = {
  navigationItems: DocumentsItem[];
  menuOpen: boolean;
  setMenuOpen: (value: boolean) => void;
};

const Navigation = ({ navigationItems, menuOpen, setMenuOpen }: NavigationProps) => {
  return (
    <nav className="relative z-40">
      <div
        className={classNames('pt-32 max-xl:fixed max-xl:inset-0 max-xl:bg-sand-100 md:pt-48 xl:pt-0', {
          'max-xl:hidden': !menuOpen,
        })}
      >
        <ul className="flex h-full overflow-auto pb-8 max-xl:mx-auto max-xl:max-w-5xl max-xl:flex-col max-xl:gap-4 max-xl:px-8 xl:h-auto xl:overflow-visible xl:pb-0">
          {navigationItems.length > 0 &&
            navigationItems[0].children?.map((item) => <NavigationItem key={item.link} level={1} {...item} />)}
        </ul>
      </div>
    </nav>
  );
};

type NavigationItemProps = DocumentsItem & {
  level: number;
  colorOverwrite?: ThemeColor;
};

const NavigationItem = ({
  title,
  link,
  target,
  active,
  current,
  spacer,
  hasSubpages,
  children,
  notInMenu,
  customProperties,
  level,
  colorOverwrite,
}: NavigationItemProps) => {
  const itemRef = useRef<HTMLLIElement>(null);
  const submenuRef = useRef<HTMLUListElement>(null);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const hasChildren = children && children.length > 0 && children.some((c) => !c.notInMenu);

  if (notInMenu) return null;

  return (
    <li
      ref={itemRef}
      className={classNames(
        'group relative xl:bg-white',
        themeColors[colorOverwrite || (customProperties?.color as ThemeColor) || 'purple']?.[600].border,
        {
          'last:border-b-2 xl:first:border-t-2 [&:last-child_a_span]:border-none': level > 1,
          'xl:[&:nth-last-child(-n+3)_>_ul]:right-0': level === 1,
          'first:mt-6': level === 2,
        }
      )}
    >
      <a
        className={classNames(
          'whitespace-nowrap bg-white text-xl max-xl:block max-xl:text-center',
          themeColors[colorOverwrite || (customProperties?.color as ThemeColor) || 'purple']?.[600].color,
          themeColors[colorOverwrite || (customProperties?.color as ThemeColor) || 'purple']?.[600].border,
          {
            'px-4 py-3 xl:py-1.5 2xl:px-5': level === 1,
            'my-[2px] border-y-2 hover:my-0 hover:border-y-4 xl:my-0': level === 1 && !active,
            'border-y-4 font-semibold': level === 1 && active,
            'hover:underline': level > 1,
          }
        )}
        href={link}
        target={target}
      >
        <span
          className={classNames(
            themeColors[colorOverwrite || (customProperties?.color as ThemeColor) || 'purple']?.[600].border,
            {
              'mx-8 block border-b py-3': level > 1,
            }
          )}
        >
          {title}
        </span>
      </a>
      {hasChildren && (
        <>
          <button
            className={classNames(
              'absolute right-2 top-3.5 flex h-8 w-8 items-center justify-center xl:hidden',
              themeColors[colorOverwrite || (customProperties?.color as ThemeColor) || 'purple']?.[600].bg
            )}
            onClick={() => setSubmenuOpen((v) => !v)}
          >
            <span className="sr-only">Untermenu öffnen</span>
            {submenuOpen ? <Minus className="fill-white p-2" /> : <Plus className="fill-white p-2" />}
          </button>
          <ul
            ref={submenuRef}
            className={classNames(
              'overflow-hidden text-center transition-[max-height] duration-300 xl:absolute xl:top-full xl:max-h-0 xl:group-hover:max-h-screen xl:group-focus:max-h-screen',
              {
                'max-h-screen': submenuOpen,
                'max-h-0': !submenuOpen,
              }
            )}
          >
            {children.map((item) => (
              <NavigationItem
                key={item.link}
                level={level + 1}
                {...item}
                colorOverwrite={customProperties?.color as ThemeColor}
              />
            ))}
          </ul>
        </>
      )}
    </li>
  );
};

export default Navigation;
